/**
 * VERTEX BUILDER
 * ==============
 * Constructor visual de agentes Vertex AI configurables.
 * Permite crear, editar y gestionar agentes sin tocar código.
 * 
 * SOLO ADMIN: davidprudencio@hotmail.com
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getSafeToken } from '../../utils/tokenHelper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import { 
  Bot, 
  ArrowLeft, 
  Plus, 
  Edit, 
  Copy, 
  Archive,
  ChevronDown,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  Users,
  BarChart3
} from 'lucide-react';

const VertexBuilder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duplicatingAgent, setDuplicatingAgent] = useState(null);
  const [archivingAgent, setArchivingAgent] = useState(null);
  const [statusFilter, setStatusFilter] = useState({ published: true, archived: false });
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);

  // Control de acceso estricto
  useEffect(() => {
    if (user?.email !== 'davidprudencio@hotmail.com') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cargar agentes
  useEffect(() => {
    if (user?.email === 'davidprudencio@hotmail.com') {
      fetchAgents();
    }
  }, [user]);

  useEffect(() => {
    if (!isStatusFilterOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsStatusFilterOpen(false);
    };

    const onPointerDown = (e) => {
      if (!statusFilterRef.current) return;
      if (statusFilterRef.current.contains(e.target)) return;
      setIsStatusFilterOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isStatusFilterOpen]);

  const fetchAgents = async () => {
    try {
      const token = getSafeToken();
      console.log('[VertexBuilder] Token obtenido:', token ? 'Presente' : 'Ausente');
      
      if (!token) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      console.log('[VertexBuilder] Headers:', JSON.stringify(headers, null, 2));

      // Para poder filtrar "Archivado", pedimos includeArchived=true (una sola vez al cargar)
      // y filtramos client-side por estado.
      const endpoint = `${VERTEX_API_BASE}/v4/agents?includeArchived=true`;
      console.log('[VertexBuilder] Endpoint:', endpoint);
      
      const response = await fetch(endpoint, { headers });
      console.log('[VertexBuilder] Response status:', response.status);

      const data = await response.json();
      console.log('[VertexBuilder] Response data:', data);
      
      if (data.success) {
        setAgents(data.agents);
      } else {
        setError('Error al cargar agentes');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Error de conexión con el backend');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (agent) => {
    navigate(`/vertex/builder/edit/${agent.agentId}`);
  };

  const handleView = (agent) => {
    navigate(`/vertex/builder/view/${agent.agentId}`);
  };

  const handleDuplicate = async (agent) => {
    if (!confirm(`¿Duplicar agente "${agent.nombreVisible}"?\n\nSe creará una copia editable con sufijo "-copy".`)) return;
    
    try {
      setDuplicatingAgent(agent.agentId);
      setError(null);
      
      const token = getSafeToken();
      if (!token) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        return;
      }

      const headers = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${VERTEX_API_BASE}/v4/agents/${agent.agentId}/duplicate`, {
        method: 'POST',
        headers
      });

      const data = await response.json();
      
      if (data.success) {
        // Recargar lista de agentes
        await fetchAgents();
        
        // Mensaje de éxito y redirección
        alert(`Agente duplicado exitosamente como "${data.agent.nombreVisible}"`);
        
        // Opcional: Redirigir al editor de la copia
        if (confirm('¿Quieres editar la copia ahora?')) {
          navigate(`/vertex/builder/edit/${data.agent.agentId}`);
        }
      } else {
        setError(data.message || 'Error al duplicar agente');
      }
    } catch (err) {
      console.error('Error duplicating agent:', err);
      setError('Error de conexión al duplicar agente');
    } finally {
      setDuplicatingAgent(null);
    }
  };

  const handleArchive = async (agent) => {
    const confirmText = `ARCHIVAR AGENTE`;
    const userInput = prompt(
      `Esta acción ARCHIVARÁ el agente "${agent.nombreVisible}"\n\n` +
      `El agente ya no estará disponible pero los datos se conservan.\n` +
      `Para confirmar, escribe exactamente: ${confirmText}`,
      ''
    );
    
    if (userInput !== confirmText) {
      if (userInput !== null) {
        alert('Texto de confirmación incorrecto. Archivado cancelado.');
      }
      return;
    }
    
    try {
      setArchivingAgent(agent.agentId);
      setError(null);
      
      const token = getSafeToken();
      if (!token) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        return;
      }

      const headers = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${VERTEX_API_BASE}/v4/agents/${agent.agentId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: 'archived' })
      });

      const data = await response.json();
      
      if (data.success) {
        // Recargar lista de agentes
        await fetchAgents();
        alert(`Agente "${agent.nombreVisible}" archivado exitosamente`);
      } else {
        setError(data.message || 'Error al archivar agente');
      }
    } catch (err) {
      console.error('Error archiving agent:', err);
      setError('Error de conexión al archivar agente');
    } finally {
      setArchivingAgent(null);
    }
  };

  const handleCreateNew = () => {
    navigate('/vertex/builder/new');
  };

  const getSimulationTypeLabel = (agent) => {
    const tags = Array.isArray(agent?.scenarioTemplates?.[0]?.tags) ? agent.scenarioTemplates[0].tags : [];
    const known = ['negotiation', 'leadership', 'conflict', 'evaluation', 'other', 'sales', 'wizard'];
    const hit = tags.find((t) => known.includes(String(t || '').trim().toLowerCase()));
    return hit ? String(hit).trim() : '—';
  };

  const formatUpdatedAt = (agent) => {
    const raw = agent?.updatedAt || agent?.createdAt;
    if (!raw) return '—';
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString();
    } catch (e) {
      return '—';
    }
  };

  const getPhaseCount = (agent) => {
    const countFromPhaseCount = Number(agent?.stateMachine?.phaseCount);
    if (Number.isFinite(countFromPhaseCount) && countFromPhaseCount >= 0) return countFromPhaseCount;
    const phases = Array.isArray(agent?.stateMachine?.phases) ? agent.stateMachine.phases : [];
    return phases.length;
  };

  const getRagLabel = (agent) => {
    const corpus = agent?.ragConfig?.primaryCorpus;
    if (typeof corpus === 'string' && corpus.trim()) return corpus.trim();
    return 'N/A';
  };

  const renderAvatar = (agent) => {
    const avatar = agent?.avatar;
    const nombre = typeof agent?.nombreVisible === 'string' ? agent.nombreVisible.trim() : '';
    const initial = nombre ? nombre.charAt(0).toUpperCase() : '';

    const isImage = typeof avatar === 'string' && (avatar.startsWith('/') || avatar.startsWith('http://') || avatar.startsWith('https://'));
    if (isImage) {
      return (
        <img
          src={avatar}
          alt="Avatar"
          className="w-9 h-9 rounded-full border border-gray-200 object-cover"
        />
      );
    }

    // Por guía de estilo del proyecto: evitar emojis en UI.
    if (initial) {
      return (
        <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
          <span className="text-sm font-semibold text-indigo-700">{initial}</span>
        </div>
      );
    }

    return (
      <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
        <Bot className="w-4 h-4 text-indigo-600" />
      </div>
    );
  };

  // Status badge
  const normalizeAgentStatus = (agent) => {
    const raw = agent?.status;
    const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';

    if (s === 'active') return 'published';
    if (s === 'inactive') return 'draft';

    if (s === 'publicado') return 'published';
    if (s === 'borrador') return 'draft';
    if (s === 'archivado') return 'archived';

    if (s === 'published' || s === 'draft' || s === 'archived') return s;

    if (agent?.isActive === true) return 'published';
    if (agent?.isActive === false) return 'draft';

    return 'draft';
  };

  const getStatusBadge = (agent) => {
    const status = normalizeAgentStatus(agent);
    const styles = {
      published: 'bg-green-100 text-green-700 border-green-200',
      draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      archived: 'bg-gray-100 text-gray-600 border-gray-200'
    };

    const icons = {
      published: <CheckCircle className="w-3 h-3" />,
      draft: <AlertCircle className="w-3 h-3" />,
      archived: <Archive className="w-3 h-3" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border ${styles[status] || styles.draft}`}>
        {icons[status]}
        {status === 'published' ? 'Publicado' : status === 'draft' ? 'Borrador' : 'Archivado'}
      </span>
    );
  };

  const filteredAgents = useMemo(() => {
    const wantPublished = Boolean(statusFilter.published);
    const wantArchived = Boolean(statusFilter.archived);

    // "Publicado" en el filtro = no archivado (incluye published y draft).
    return agents.filter((agent) => {
      const s = normalizeAgentStatus(agent);
      const isArchived = s === 'archived';
      if (isArchived) return wantArchived;
      return wantPublished;
    });
  }, [agents, statusFilter]);

  return (
    <div className="h-full bg-gradient-to-br from-indigo-50 via-white to-purple-50 overflow-y-auto">
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                  <span className="hidden sm:inline">VRT Lab Builder</span>
                  <span className="sm:hidden">Builder</span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Constructor de Agentes Vertex AI</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/admin/config')}
                className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-white border-2 border-gray-600 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                title="Config Admin - Usuarios, Asignaciones y Analytics"
              >
                <Users className="w-5 h-5" />
                <span className="hidden lg:inline">Config Admin</span>
              </button>
              
              <button
                onClick={handleCreateNew}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Crear Agente</span>
                <span className="sm:hidden">Crear</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        )}

        {/* Lista de agentes */}
        {!loading && agents.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Agentes Existentes ({filteredAgents.length} de {agents.length})
              </h2>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Nombre</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">SimulationType</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="relative inline-flex" ref={statusFilterRef}>
                          <button
                            type="button"
                            onClick={() => setIsStatusFilterOpen((v) => !v)}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                            aria-haspopup="menu"
                            aria-expanded={isStatusFilterOpen}
                            title="Filtrar por estado"
                          >
                            Estado
                            <ChevronDown className="w-4 h-4" />
                          </button>

                          {isStatusFilterOpen && (
                            <div
                              role="menu"
                              className="absolute left-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20"
                            >
                              <label className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-md cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4"
                                  checked={Boolean(statusFilter.published)}
                                  onChange={(e) => setStatusFilter((prev) => ({ ...prev, published: e.target.checked }))}
                                />
                                Publicados
                              </label>
                              <label className="flex items-center gap-2 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 rounded-md cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4"
                                  checked={Boolean(statusFilter.archived)}
                                  onChange={(e) => setStatusFilter((prev) => ({ ...prev, archived: e.target.checked }))}
                                />
                                Archivados
                              </label>
                            </div>
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">UpdatedAt</th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredAgents.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                          No hay agentes que coincidan con el filtro seleccionado.
                        </td>
                      </tr>
                    )}
                    {filteredAgents.map((agent) => {
                      const isDuplicating = duplicatingAgent === agent.agentId;
                      const isArchiving = archivingAgent === agent.agentId;
                      const simType = getSimulationTypeLabel(agent);

                      return (
                        <tr key={agent._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-gray-900">{agent.nombreVisible}</div>
                            <div className="text-xs text-gray-500 line-clamp-1">{agent.descripcion}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs font-mono text-gray-700">{simType}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {getStatusBadge(agent)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs text-gray-700">{formatUpdatedAt(agent)}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-2">
                              <button
                                onClick={() => handleEdit(agent)}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                                title="Editar agente"
                              >
                                <Edit className="w-4 h-4" />
                                Editar
                              </button>

                              <button
                                onClick={() => handleView(agent)}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors"
                                title="Ver agente"
                              >
                                <Eye className="w-4 h-4" />
                                Ver
                              </button>

                              <button
                                onClick={() => handleDuplicate(agent)}
                                disabled={isDuplicating}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Duplicar agente"
                              >
                                {isDuplicating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                                <span className="hidden lg:inline">{isDuplicating ? 'Duplicando…' : 'Duplicar'}</span>
                              </button>

                              <button
                                onClick={() => handleArchive(agent)}
                                disabled={isArchiving}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Archivar agente"
                              >
                                {isArchiving ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Archive className="w-4 h-4" />
                                )}
                                <span className="hidden lg:inline">{isArchiving ? 'Archivando…' : 'Archivar'}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Estado vacío */}
        {!loading && agents.length === 0 && (
          <div className="text-center py-20">
            <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay agentes creados
            </h3>
            <p className="text-gray-600 mb-6">
              Crea tu primer agente para empezar
            </p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              Crear Primer Agente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VertexBuilder;
